import { useEffect, useState } from "react";

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** Images older than this are considered stale for the `?showOld` filter. */
export const STALE_THRESHOLD_MS = 15 * MINUTE_MS;

/**
 * Reads a camera/direction object's `lastUpdated` ISO-8601 timestamp, gateway-wide as of
 * the cameraReport.json/showCamera.json/tripRouteCameras.json rollout - no legacy age
 * fields (`age`/`imageAge`/`ageMs`/`updatedMessage`) left to fall back to.
 */
export const resolveLastUpdated = (source) => source?.lastUpdated ?? null;

/**
 * Formats the age of a `lastUpdated` ISO-8601 timestamp as of now, rolling up to days and
 * hours once they're present instead of always reporting in minutes/seconds, and returns
 * the numeric age in milliseconds for staleness thresholds.
 */
export const describeAge = (lastUpdated) => {
  if (!lastUpdated) return { text: "Unknown", ms: null };
  const ms = Date.now() - new Date(lastUpdated).getTime();
  if (Number.isNaN(ms) || ms < 0) return { text: "Unknown", ms: null };

  const plural = (value, name) => `${value} ${name}${value === 1 ? "" : "s"}`;

  const days = Math.floor(ms / DAY_MS);
  if (days > 0) {
    const hours = Math.floor((ms % DAY_MS) / HOUR_MS);
    return { text: `${plural(days, "day")}, ${plural(hours, "hour")} ago`, ms };
  }
  const hours = Math.floor(ms / HOUR_MS);
  if (hours > 0) {
    const minutes = Math.floor((ms % HOUR_MS) / MINUTE_MS);
    return {
      text: `${plural(hours, "hour")}, ${plural(minutes, "minute")} ago`,
      ms,
    };
  }
  const minutes = Math.floor(ms / MINUTE_MS);
  const seconds = Math.floor((ms % MINUTE_MS) / SECOND_MS);
  if (minutes > 0) {
    return {
      text: `${plural(minutes, "minute")}, ${plural(seconds, "second")} ago`,
      ms,
    };
  }
  return { text: `${plural(seconds, "second")} ago`, ms };
};

/**
 * Forces a re-render every `intervalMs` so age text/thresholds computed from `describeAge`
 * stay current between data fetches instead of freezing at whatever the last response said.
 */
export const useAgeTicker = (intervalMs = 15000) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return tick;
};
